import React from 'react'
import { Alert, Box, Typography } from '@mui/material'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import { blue, grey } from '../../colors'
import { ADDED_QUESTION_TYPES, type AddedQuestionType } from '../../consts/addedQuestionTypes'

/**
 * CR-295 — switching between a course section's **shared** question set and the instructor's own.
 *
 * A switcher rather than two panes on one screen, and that is a constraint rather than a layout
 * preference: `SurveyTemplateSlice` holds a *singleton* editable survey, and this screen loads
 * exactly one set into it, chosen by the `type` search param. Two simultaneously-editable sets
 * would be a change of ownership over shared survey-builder state, which is a much larger change
 * than this feature needs.
 *
 * It also means the tab you are on already tells you whose questions you are editing, so there are
 * no per-question SHARED / MINE chips: with one set loaded at a time, every question in view has
 * the same owner and a per-row badge would repeat the tab label on every line. The spec's chips
 * were designed against an earlier both-sets-visible layout — see the scope note in
 * docs/specs/active/CR-295-section-added-questions/spec.md.
 *
 * Spec: docs/specs/active/CR-295-section-added-questions/spec.md (surface 3)
 */

interface SharedQuestionSetSwitcherProps {
  activeType: AddedQuestionType
  sharedCount: number
  mineCount: number
  onSelect: (type: AddedQuestionType) => void
}

const TABS: Array<{ type: AddedQuestionType, label: string }> = [
  { type: ADDED_QUESTION_TYPES.SECTION, label: 'Shared' },
  { type: ADDED_QUESTION_TYPES.INSTRUCTOR, label: 'Mine' }
]

export const SharedQuestionSetSwitcher: React.FC<SharedQuestionSetSwitcherProps> = ({
  activeType,
  sharedCount,
  mineCount,
  onSelect
}) => {
  const countFor = (type: AddedQuestionType): number =>
    type === ADDED_QUESTION_TYPES.SECTION ? sharedCount : mineCount

  return (
    <Box
      role="group"
      aria-label="Question set"
      display="flex"
      width="fit-content"
      mx="auto"
      mt={4}
      p={0.5}
      gap={0.5}
      sx={{ backgroundColor: grey[100], borderRadius: '8px' }}
    >
      {TABS.map(({ type, label }) => {
        const isActive = type === activeType
        return (
          <Box
            key={type}
            component="button"
            type="button"
            aria-pressed={isActive}
            onClick={() => { onSelect(type) }}
            sx={{
              px: 3,
              py: 1.5,
              border: 'none',
              cursor: 'pointer',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: isActive ? 600 : 500,
              color: isActive ? grey[950] : grey[850],
              backgroundColor: isActive ? grey[0] : 'transparent',
              boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
              '&:hover': { backgroundColor: isActive ? grey[0] : grey[200] },
              '&:focus-visible': { outline: `2px solid ${blue[500]}`, outlineOffset: '1px' }
            }}
          >
            {`${label} (${countFor(type)})`}
          </Box>
        )
      })}
    </Box>
  )
}

/**
 * The disclosure on the shared tab. Unannounced shared editing is the second problem this feature
 * was reported for, so this is a requirement rather than polish: an instructor editing here is
 * editing for everyone teaching the section, and nothing else on the screen says so.
 *
 * It deliberately names no instructors and gives no count, unlike the spec's wording ("all 4
 * instructors on this section"). Neither is available to this screen: the dashboard card payload
 * carries no instructor list, and `/api/enrollments/?section_id=` returns students. Saying "every
 * instructor on this course section" carries the same meaning without inventing a number — see the
 * open backend ask in the spec.
 */
interface SharedQuestionsNoticeProps {
  /**
   * Whether the switcher is on screen. The closing sentence names the **Mine** tab, so it may only
   * render where that tab exists — on a Course Section-only project the switcher is hidden and the
   * pointer would aim at a control that is not there. The disclosure itself still applies either
   * way, which is why only the sentence is conditional and not the notice.
   */
  hasMineTab: boolean
}

export const SharedQuestionsNotice: React.FC<SharedQuestionsNoticeProps> = ({ hasMineTab }) => (
  <Box maxWidth={800} mx="auto" mt={4}>
    <Alert severity="info" icon={<GroupsOutlinedIcon fontSize="inherit" />}>
      <Typography fontSize="14px" fontWeight={600}>
        Shared with every instructor on this course section.
      </Typography>
      <Typography fontSize="14px" color={grey[850]}>
        Anything you add, change or delete here changes it for every one of them.
        {hasMineTab && <> Questions only you should see belong under <strong>Mine</strong>.</>}
      </Typography>
    </Alert>
  </Box>
)
